import { IStudent } from '@studyportals/studentdomain';
import {
	BestFitResult,
	ScoreDeterminantType,
} from '@studyportals/bestfit-api-fe-client';
import { BestfitRecalculatedEvent } from 'interfaces';
import { BestFitRepository } from '../domain/best-fit-repository';
import { StudyStoreState } from '../enums/study-store-state';
import { WarningMessages } from '../enums/warning-messages';
import { IStudyInformation } from '../interfaces/study-information.interface';
import { StudyRepository } from '../domain/study-repository';
import { StudentField } from '@studyportals/studentdomain';
import { StudentRepository } from '../domain/student-repository';
import { IActions } from '@/interfaces/store/actions.interfaces';
import { store } from './store';
import globals from '@/utils/globals';
import { IStudyStore } from '@/interfaces/store/study-store/study-store.store.interface';
import { IStudyStoreState } from '@/interfaces/store/study-store/study-store.state.interface';
import { IStudyStoreGetters } from '@/interfaces/store/study-store/study-store.getters.interface';
import { IStudyStoreMutations } from '@/interfaces/store/study-store/study-store.mutations.interface';
import { IStudyStoreActions } from '@/interfaces/store/study-store/study-store.actions.interface';
import { BestFitOpenerPlugin } from './plugins/bestfit-opener-plugin';
import { LoginStatusCheckerPlugin } from './plugins/login-status-checker-plugin';
import { ProfileDataCheckerPlugin } from './plugins/profile-data-checker-plugin';
import { studyStoreActions } from './study-store/study-store.actions';
import { studyStoreGetters } from './study-store/study-store.getters';
import { studyStoreMutations } from './study-store/study-store.mutations';
import { studyStoreState } from './study-store/study-store.state';
import { actionHelpers } from './action-helpers';
import { ChatType, CopilotStudyLevel, RenderChatCopilotCommand } from '@studyportals/copilot-service-interfaces';
import LocalizedContentPlugin from './plugins/localized-content-plugin';
import CurrencyConversionPlugin from './plugins/currency-conversion-plugin';

const actions: IActions = {
	async setAreDependenciesLoaded(areDependenciesLoaded: boolean) {
		if (areDependenciesLoaded) {
			await new LoginStatusCheckerPlugin().initiate();
			new LocalizedContentPlugin().initiate();
			new CurrencyConversionPlugin().initiate();

			globals.studyRepository = new StudyRepository();
			globals.bestFitRepository = new BestFitRepository();
			globals.studentRepository = new StudentRepository();

			await new ProfileDataCheckerPlugin().initiate();
			new BestFitOpenerPlugin().initiate();
		}

		store.mutations.setAreDependenciesLoaded(areDependenciesLoaded);
	},
	registerStudyStore(studyId: number, moduleInitState: StudyStoreState) {
		const _state: IStudyStoreState = studyStoreState(studyId, moduleInitState);
		const _getters: IStudyStoreGetters = studyStoreGetters(_state);
		const _mutations: IStudyStoreMutations = studyStoreMutations(_state);
		const _actions: IStudyStoreActions = studyStoreActions(_mutations);

		const module: IStudyStore = {
			state: _state,
			getters: _getters,
			mutations: _mutations,
			actions: _actions,
		};

		store.mutations.setNewStudyStore(studyId, module);
	},
	setLoggedInState(loggedInState: boolean) {
		store.mutations.setLoggedInState(loggedInState);

		if (!loggedInState) {
			store.actions.convertStudyStoreState({
				from: StudyStoreState.CREATED,
				to: StudyStoreState.READY,
			});
		}
	},
	toggleActiveDeterminant(determinant: ScoreDeterminantType) {
		if (
			determinant === ScoreDeterminantType.ACADEMIC_FIT ||
			determinant === ScoreDeterminantType.BUDGET_FIT
		) {
			const mainDeterminent = store.getters.activeMainDeterminant();

			if (mainDeterminent === determinant) {
				store.mutations.setActiveMainDeterminant(null);
				return;
			}

			store.mutations.setActiveMainDeterminant(determinant);
			return;
		}

		const subDeterminant = store.getters.activeSubDeterminant();

		if (subDeterminant === determinant) {
			store.mutations.setActiveSubDeterminant(null);
			return;
		}
		store.mutations.setActiveSubDeterminant(determinant);
	},

	incentiveClicked(): void {

		const studyId = store.getters.activeStudyId();

		if (!studyId) {
			return;
		}

		const command = new RenderChatCopilotCommand(ChatType.BESTFIT, CopilotStudyLevel.MASTER, undefined, undefined, undefined, studyId)
		globals.copilotServiceApplication.renderChat(command);

	},
	openOverview(): void {
		globals.eventBus.emit('openPopup');
	},
	closeOverview(): void {
		globals.eventBus.emit('closePopup');
	},
	setQuestionnaireCompletedListener(): void {
		document.addEventListener('questionnaire_completed', (event: any) => {
			if (event.detail.identifier !== 'bestfit') {
				return;
			}

			if (!store.getters.activeStudyId()) {
				store.actions.setStudyIdFromUrl();
			}

			store.actions.openOverview();
		});
	},
	setStudyIdFromUrl(): void {
		let studyID: number | string = window.location.pathname.split('/')[2];
		studyID = parseInt(studyID, 10);

		if (!isNaN(studyID)) {
			store.mutations.setActiveStudyId(studyID);
		}
	},
	async studentDataUpdated(changes?: IStudent): Promise<void> {
		store.actions.convertStudyStoreState({
			from: StudyStoreState.READY,
			to: StudyStoreState.PENDING,
		});

		store.actions.convertStudyStoreState({
			from: StudyStoreState.CALCULATED,
			to: StudyStoreState.PENDING,
		});

		await store.actions.checkStudentFieldUpdates(changes);
		await store.actions.calculateFit();

		globals.eventAggregationService.publishTo(
			BestfitRecalculatedEvent.EventType,
			new BestfitRecalculatedEvent(),
		);
	},

	async checkStudentFieldUpdates(changes?: IStudent): Promise<void> {
		if (!changes) return;
		if (!Object.prototype.hasOwnProperty.call(changes, StudentField.TUITION_BUDGET) &&
			!Object.prototype.hasOwnProperty.call(changes, StudentField.NATIONALITY_COUNTRY_ID) &&
			!Object.prototype.hasOwnProperty.call(changes, StudentField.RESIDENCE_COUNTRY_ID) &&
			!Object.prototype.hasOwnProperty.call(changes, StudentField.CURRENCY) &&
			!Object.prototype.hasOwnProperty.call(changes, StudentField.LIVING_BUDGET) &&
			!Object.prototype.hasOwnProperty.call(changes, StudentField.WORK_EXPERIENCE) &&
			!Object.prototype.hasOwnProperty.call(changes, StudentField.GPA)) {
			return;
		}

		const userData = await globals.studentRepository.getFields([
			StudentField.TUITION_BUDGET,
			StudentField.NATIONALITY_COUNTRY_ID,
			StudentField.RESIDENCE_COUNTRY_ID,
			StudentField.CURRENCY,
			StudentField.LIVING_BUDGET,
			StudentField.WORK_EXPERIENCE,
			StudentField.GPA,
		]);

		if (!userData) return;

		const {
			tuition_budget,
			nationality_country_id,
			residence_country_id,
			currency,
			living_budget,
			work_experience,
			gpa,
		} = userData;

		store.mutations.setTuitionBudget(tuition_budget);
		store.mutations.setUserNationality(nationality_country_id);
		store.mutations.setUserCountryOfResidence(residence_country_id);
		store.mutations.setCurrency(currency);
		store.mutations.setLivingBudget(living_budget);
		store.mutations.setWorkExperience(work_experience);
		store.mutations.setGpa(gpa);
	},
	convertStudyStoreState({ from, to }): void {
		store.getters.getAllStudyStores().forEach((studyStore) => {
			if (studyStore.getters.storeState() !== from) {
				return;
			}

			studyStore.mutations.setStoreState(to as StudyStoreState);
		});
	},
	async calculateFit(): Promise<void> {
		if (!store.getters.userLoggedIn()) {
			console.warn(WarningMessages.CALCULATE_FIT_WHILE_NOT_LOGGED_IN);
			return;
		}

		if (store.getters.bestFitCompleteness() !== 100) {
			console.warn(WarningMessages.CALCULATE_FIT_WHILE_COMPLETENESS_NOT_100);
			return;
		}

		store.actions.convertStudyStoreState({
			from: StudyStoreState.FAILED,
			to: StudyStoreState.PENDING,
		});

		const studyIdsToCalculate: number[] = actionHelpers.getPendingStudies();

		if (studyIdsToCalculate.length <= 0) {
			store.actions.convertStudyStoreState({
				from: StudyStoreState.PENDING,
				to: StudyStoreState.FAILED,
			});
			return;
		}

		try {
			const fits: Map<number,Map<ScoreDeterminantType, BestFitResult>> = await globals.bestFitRepository.combinedCalculation(
				studyIdsToCalculate,
				[ScoreDeterminantType.OVERALL_FIT],
			);

			actionHelpers.handleFailedStudies(fits, studyIdsToCalculate);

			for (const [studyId, fit] of fits) {
				store.getters.getStudyStore(studyId)?.actions.saveFit(fit);
			}

			document.dispatchEvent(
				new CustomEvent('bestfit_fit_given', {
					detail: fits,
				}),
			);
		} catch (error) {
			console.warn(error);
		}
	},
	async getStudyInformation(studyId): Promise<void> {
		const studyStore = store.getters.getStudyStore(studyId);
		if (
			studyStore?.getters.studyTitle() &&
			studyStore.getters.universityTitle()
		) {
			return;
		}

		const studyInformation: IStudyInformation = await globals.studyRepository.get(studyId);

		studyStore?.mutations.saveStudyInformation(
			studyInformation.studyTitle,
			studyInformation.universityTitle,
			studyInformation.referralLink,
			studyInformation.isPremium,
			studyInformation.organizationId,
		);
	},
};

export { actions };
